import {
  getGetWorkspacesApiV1QueryKey,
  useDeleteWorkspaceApiV1,
  useGetWorkspaceMeApiV1Suspense,
  useLeaveWorkspaceApiV1,
  useUpdateWorkspaceApiV1,
} from "@/api/__generated__/api.schema";
import { WorkspaceMemberRole } from "@/api/__generated__/models";
import { uploadFile } from "@/api/remotes/upload-file";
import { Allowed } from "@/components/role/allowed";
import { WorkspaceIcon } from "@/components/workspace-icon";
import { routes } from "@/constants/url";
import { useWorkspace, useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useBlocker } from "@/hooks/use-blocker";
import { useImageUploader } from "@/hooks/use-file-selector";
import { cn } from "@/lib/utils";
import { useToastMessage } from "@backpac/monica";
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  useConfirm,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

export function WorkspaceGeneralSetting() {
  const { info } = useToastMessage();
  const queryClient = useQueryClient();
  const { data: workspace } = useWorkspace();
  const {
    data: {
      data: { item: me },
    },
  } = useGetWorkspaceMeApiV1Suspense(workspace.id);

  const { mutate: updateWorkspace, isPending } = useUpdateWorkspaceApiV1({
    mutation: {
      onSuccess: () => {
        info("Your settings have been updated successfully");
        queryClient.invalidateQueries({ queryKey: useWorkspace.queryKey(workspace.id) });
      },
    },
  });

  return (
    <div className="space-y-6">
      <WorkspaceGeneralSetting.Field label="Details" description="Edit workspace name and logo.">
        <WorkspaceDetailsForm
          defaultValues={{
            workspaceName: workspace.name,
          }}
          defaultThumbnailUrl={workspace.thumbnailUrl}
          onSubmit={async (values) => {
            let thumbnailUrl: string | null = workspace.thumbnailUrl ?? null;

            if (values.workspaceLogoImage === null) {
              thumbnailUrl = null;
            } else if (values.workspaceLogoImage != null) {
              const { data } = await uploadFile(values.workspaceLogoImage);
              thumbnailUrl = data.url;
            }

            updateWorkspace({
              workspaceId: workspace.id,
              data: {
                name: values.workspaceName,
                thumbnailUrl,
              },
            });
          }}
          isLoading={isPending}
          disabled={me.role === WorkspaceMemberRole.MEMBER}
        />
      </WorkspaceGeneralSetting.Field>
      <div className="flex gap-3">
        <Allowed.OwnerOnly fallback={<LeaveWorkspaceButton />}>
          <LeaveWorkspaceButton.NotAllowed />
        </Allowed.OwnerOnly>
        <Allowed role={WorkspaceMemberRole.OWNER} fallback={<DeleteWorkspaceButton.NotAllowed />}>
          <DeleteWorkspaceButton />
        </Allowed>
      </div>
    </div>
  );
}

const formSchema = z.object({
  workspaceName: z.string().min(1, "Workspace name is required"),
  workspaceLogoImage: z.instanceof(File).nullish(),
});

type FormValues = z.infer<typeof formSchema>;

interface WorkspaceDetailFormProps {
  defaultValues?: FormValues;
  onSubmit: (values: FormValues) => Promise<void>;
  isLoading?: boolean;
  defaultThumbnailUrl?: string;
  disabled?: boolean;
}

function WorkspaceDetailsForm({
  defaultValues,
  onSubmit,
  isLoading,
  defaultThumbnailUrl,
  disabled,
}: WorkspaceDetailFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useBlocker(() => {
    return form.formState.isDirty;
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="workspaceName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Workspace name</FormLabel>
                <Input {...field} className="w-full" disabled={disabled} />
              </FormItem>
            )}
          />

          <div
            className={cn("p-6 rounded-[12px] border border-borderLight space-y-4", disabled && "bg-backgroundLight")}
          >
            <Typography size="xs" weight={600} color="textPrimary">
              Workspace logo
            </Typography>
            <div className={cn("space-y-2")}>
              <WorkspaceDetailsForm.WorkspaceIconUpload
                defaultValue={defaultThumbnailUrl}
                workspaceName={defaultValues?.workspaceName}
                form={form}
                defaultValues={defaultValues}
                disabled={disabled}
              />

              <Typography size="paragraphXs" color="textMuted" className="italic">
                Recommended size is 128 × 128px
              </Typography>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={!form.formState.isDirty} loading={isLoading}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
}

interface WorkspaceIconUploadProps {
  defaultValue?: string;
  workspaceName?: string;
  form: ReturnType<typeof useForm<FormValues>>;
  defaultValues?: FormValues;
  className?: string;
  defaultThumbnailUrl?: string;
  disabled?: boolean;
}

WorkspaceDetailsForm.WorkspaceIconUpload = function WorkspaceIconUpload({
  defaultValue,
  workspaceName,
  form,
  disabled,
}: WorkspaceIconUploadProps) {
  const [uploaded, controls] = useImageUploader();

  const handleImageUpload = useCallback(async () => {
    const file = await controls.upload();
    if (file != null) {
      form.setValue("workspaceLogoImage", file, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, controls]);
  const handleImageReset = useCallback(async () => {
    controls.reset();
    form.setValue("workspaceLogoImage", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, controls]);

  const previewUrl = uploaded.previewUrl ?? (uploaded.status.isDirty ? undefined : defaultValue);

  return (
    <Dropdown>
      <DropdownTrigger disabled={disabled}>
        <div className={cn("relative flex items-center gap-3", disabled && "cursor-not-allowed")}>
          <WorkspaceIcon className="size-8" workspaceName={workspaceName} thumbnailUrl={previewUrl} />
          <i className="i-bp-chevron-down w-4 h-4" />
        </div>
      </DropdownTrigger>
      <DropdownContent>
        <DropdownItem onClick={() => handleImageUpload()}>
          <Typography size="sm" weight={600}>
            Upload image
          </Typography>
        </DropdownItem>
        <DropdownItem onClick={() => handleImageReset()}>
          <Typography size="sm" weight={600}>
            Remove image
          </Typography>
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};

function LeaveWorkspaceButton({ className }: { className?: string }) {
  const { info } = useToastMessage();
  const { confirm } = useConfirm();
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: leaveWorkspace, isPending } = useLeaveWorkspaceApiV1({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: useWorkspace.queryKey(workspaceId) }),
          queryClient.invalidateQueries({ queryKey: getGetWorkspacesApiV1QueryKey() }),
        ]);
        info("You have left the workspace successfully");
        navigate(routes.WORKSPACE_NAVIGATOR.root);
      },
    },
  });

  const handleLeaveWorkspace = useCallback(async () => {
    if (
      await confirm({
        title: "Leave workspace",
        description:
          "You will lose access to all data in this workspace. You can rejoin if you're invited back. Are you sure you want to leave?",
        confirmText: "Leave",
        confirmVariant: "error",
        cancelText: "Cancel",
      })
    ) {
      leaveWorkspace({ workspaceId });
    }
  }, [leaveWorkspace, workspaceId, confirm]);

  return (
    <Button variant="outlined" onClick={handleLeaveWorkspace} loading={isPending} className={className}>
      Leave workspace
    </Button>
  );
}

LeaveWorkspaceButton.NotAllowed = function LeaveWorkspaceButtonNotAllowed() {
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button variant="outlined" disabled={true}>
          Leave workspace
        </Button>
      </TooltipTrigger>
      <TooltipContent position="topMiddle" side="bottom">
        You can't leave this workspace because you're the owner. Transfer ownership before leaving.
      </TooltipContent>
    </Tooltip>
  );
};

function DeleteWorkspaceButton({ className }: { className?: string }) {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  const { mutate: deleteWorkspace, isPending } = useDeleteWorkspaceApiV1({
    mutation: {
      onSuccess: () => {
        navigate(routes.AUTH.signup);
      },
    },
  });

  const handleDeleteWorkspace = useCallback(async () => {
    const isConfirmed = await confirm({
      title: "Delete workspace",
      addon: <i className="inline-block i-bp-warning w-6 h-6 text-colorRedDark" />,
      description:
        "This action will permanently delete the workspace, along with all its members and data. Are you sure you want to delete?",
      confirmText: "Delete",
      confirmVariant: "error",
      cancelText: "Cancel",
    });

    if (isConfirmed) {
      deleteWorkspace({ workspaceId });
    }
  }, [confirm, deleteWorkspace, workspaceId]);

  return (
    <Button variant="error" onClick={handleDeleteWorkspace} loading={isPending} className={className}>
      Delete workspace
    </Button>
  );
}

DeleteWorkspaceButton.NotAllowed = function DeleteWorkspaceButtonNotAllowed() {
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button variant="error" disabled={true}>
          Delete workspace
        </Button>
      </TooltipTrigger>
      <TooltipContent position="topMiddle" side="bottom">
        Only a workspace owner can delete the workspace.
      </TooltipContent>
    </Tooltip>
  );
};

interface WorkspaceGeneralSettingFieldProps {
  label: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}

WorkspaceGeneralSetting.Field = function WorkspaceGeneralSettingField({
  label,
  description,
  children,
  className,
}: WorkspaceGeneralSettingFieldProps) {
  return (
    <div className={cn("flex flex-col gap-6 lg:flex-row", className)}>
      <div className="flex flex-col gap-2 w-[344px]">
        <Typography size="md" weight={600} color="textPrimary">
          {label}
        </Typography>
        <Typography size="paragraphXs" color="textMuted">
          {description}
        </Typography>
      </div>
      <div className="bg-white flex-1 p-8 rounded-lg shadow-standard">{children}</div>
    </div>
  );
};
